import { useState, useMemo, useCallback } from "react";

interface IUsePaginationReturn<T> {
	page: number;
	currentItems: T[];
	totalItems: number;
	itemsPerPage: number;
	totalPages: number;
	hasNext: boolean;
	hasPrev: boolean;
	handlePageChange: (newPage: number) => void;
	reset: () => void;
}

/**
 * ページネーションのロジックを共通化するカスタムフック
 * @param items - ページネーション対象のアイテム配列
 * @param itemsPerPage - 1ページあたりのアイテム数
 * @returns ページネーションに必要な状態と関数
 */
export function usePagination<T>(
	items: T[],
	itemsPerPage: number,
): IUsePaginationReturn<T> {
	const [page, setPage] = useState(1);

	const totalItems = items.length;
	const totalPages = Math.ceil(totalItems / itemsPerPage);

	// itemsが変わった時にページが範囲外になる場合は調整
	const validPage = useMemo(() => {
		if (totalPages === 0) return 1;
		return Math.min(page, totalPages);
	}, [page, totalPages]);

	// 現在のページに表示するアイテム
	const currentItems = useMemo(() => {
		const startIndex = (validPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return items.slice(startIndex, endIndex);
	}, [items, validPage, itemsPerPage]);

	const hasNext = validPage < totalPages;
	const hasPrev = validPage > 1;

	const handlePageChange = useCallback(
		(newPage: number) => {
			if (newPage >= 1 && newPage <= totalPages) {
				setPage(newPage);
			}
		},
		[totalPages],
	);

	const reset = useCallback(() => {
		setPage(1);
	}, []);

	return {
		page: validPage,
		currentItems,
		totalItems,
		itemsPerPage,
		totalPages,
		hasNext,
		hasPrev,
		handlePageChange,
		reset,
	};
}

export default usePagination;
